/* Workforce attendance — workers on the floor against the requirement
 * sheet, from the register and the biometric report.
 *
 * One page shown twice in the portal: `view="staffing"` is who is on the
 * roll against the sheet, `view="attendance"` who turned up day by day. The
 * design keys the same split off `?view=`; `all` is its unparameterised
 * page, every tab in one.
 *
 * One month is fetched whole; the day range, the department and the
 * attendance target are applied here, so moving any of them redraws every
 * tab without another request.
 *
 * The People picker (workers or staff) is the app's own, not the design's:
 * the design knows workers only. It is a second fetch, since the two
 * groups are different people against different lines of the requirement
 * sheet; `people` is the noun every tab words itself from. */

import { useCallback, useMemo, useState } from 'react'
import { MAIN_STYLE, Subline } from '../App'
import { attendance } from '../lib/api_attendance'
import type { AttMonth, Who } from '../lib/api_attendance'
import { useAsync } from '../lib/useAsync'
import { Empty, ErrorBox, KpiRow, Loading, Segmented } from '../components/ui'
import type { Kpi } from '../components/ui'
import { num } from '../lib/format'
import { useIsDark } from '../lib/theme'
import { scopeDesig, scopeMonth } from './attendance/model'
import type { DesigScope, Scope } from './attendance/model'
import { heads, signed, words } from './attendance/parts'
import type { People } from './attendance/parts'
import { StaffingTab } from './attendance/StaffingTab'
import { DaysTab } from './attendance/DaysTab'
import { DepartmentsTab } from './attendance/DepartmentsTab'
import type { DeptUi } from './attendance/DepartmentsTab'
import { WorkersTab } from './attendance/WorkersTab'
import type { WorkerUi } from './attendance/WorkersTab'
import { DataTab } from './attendance/DataTab'

type Tab = 'man' | 'days' | 'dept' | 'exc' | 'data'
export type View = 'staffing' | 'attendance' | 'all'

const TABS: { value: Tab; label: string }[] = [
  { value: 'man', label: 'Staffing summary' },
  { value: 'days', label: 'Attendance' },
  { value: 'dept', label: 'Departments' },
  { value: 'exc', label: 'Workers & register' },
  { value: 'data', label: 'Data files' },
]

/** The tabs each portal page keeps; `all` keeps them all. */
const VIEW_TABS: Partial<Record<View, Tab[]>> = {
  staffing: ['man', 'data'],
  attendance: ['days', 'dept', 'exc', 'data'],
}

/** The attendance target the page opens on, per cent. */
const TARGET = 85

export default function Attendance({ view = 'all' }: { view?: View }) {
  const [tab, setTab] = useState<Tab>(view === 'attendance' ? 'days' : 'man')
  const [who, setWho] = useState<Who>('worker')
  const [month, setMonth] = useState<string | undefined>(undefined)
  const [range, setRange] = useState<{ from: number | null; to: number | null }>({ from: null, to: null })
  const [dept, setDept] = useState<string | null>(null)
  const [target, setTarget] = useState(TARGET)
  // Kept here rather than in the tabs so a sort or an open department is
  // still there after a look at another tab.
  const [deptUi, setDeptUi] = useState<DeptUi>({ sort: 'avgPresent', desc: true, open: null })
  const [workerUi, setWorkerUi] = useState<WorkerUi>({
    list: 'att', limit: 20, missSort: 'n', lateSort: 'pct',
  })
  // Which departments of the By-designation table are open.
  const [desigOpen, setDesigOpen] = useState<Record<string, boolean>>({})
  const dark = useIsDark()

  const { data, error, loading, reload } = useAsync(() => attendance.summary(month, who), [month, who])
  const D: AttMonth | null = data && !data.empty ? data : null
  const people: People = who === 'staff' ? 'staff' : 'workers'
  const S = useMemo(
    () => (D ? scopeMonth(D, dept, range.from, range.to) : null),
    [D, dept, range.from, range.to])
  const G = useMemo(() => (D && S ? scopeDesig(D, S) : null), [D, S])

  // The staffing summary is about the roll, not the day's presence: it
  // takes the roll KPIs and drops the by-shift, shift × day and findings
  // panels, which the design keys off the same test.
  const tabs = TABS.filter((t) => !VIEW_TABS[view] || VIEW_TABS[view]!.includes(t.value))
    // Staff have a report, not a register.
    .map((t) => (t.value === 'exc' && people === 'staff' ? { ...t, label: 'Staff & report' } : t))
  // The portal mounts this component at both routes, so a switch between
  // them can arrive carrying the other page's tab.
  const shown = tabs.some((t) => t.value === tab) ? tab : tabs[0].value
  const staffing = view === 'staffing' || (view === 'all' && shown === 'man')

  const pickMonth = useCallback((m: string) => {
    setMonth(m)
    setRange({ from: null, to: null })
    setDeptUi((u) => ({ ...u, open: null }))
  }, [])
  const afterLoad = useCallback((m: string | undefined) => {
    if (m) pickMonth(m)
    reload()
  }, [pickMonth, reload])
  // The other group has its own departments and its own days on file, so
  // the department and the day range start over; the month is kept, and
  // the server falls back to the group's latest if it has no such month.
  const pickWho = useCallback((w: Who) => {
    setWho(w)
    setDept(null)
    setRange({ from: null, to: null })
    setDeptUi((u) => ({ ...u, open: null }))
  }, [])

  return (
    <>
      <div className="no-print" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 20, flexWrap: 'wrap', padding: '14px 32px',
        borderBottom: '1px solid var(--color-divider)',
      }}>
        <Segmented<Tab> value={shown} minWidth={156} onChange={setTab} options={tabs} />
        {data ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
            <Picker label="People">
              <Segmented<Who> value={who} minWidth={84} onChange={pickWho}
                options={[{ value: 'worker', label: 'Workers' }, { value: 'staff', label: 'Staff' }]} />
            </Picker>
            {D && S ? <Pickers D={D} S={S} range={range} setRange={setRange}
              pickMonth={pickMonth} setDept={setDept} /> : null}
          </div>
        ) : null}
      </div>

      <main style={MAIN_STYLE}>
        {error ? <ErrorBox error={error} onRetry={reload} /> : null}
        {loading && !data ? <Loading what="the attendance on file" /> : null}

        {data?.empty ? (
          <>
            <Subline>
              {people === 'staff' ? 'Staff biometric report' : 'Workers’ register and biometric report'} · nothing on file yet
            </Subline>
            <Empty title={`No ${people} attendance on file yet`}>
              {people === 'staff'
                ? 'Load the staff biometric report below and every tab fills in.'
                : 'Load the workers’ register and the biometric reports below and every tab fills in.'}
            </Empty>
            <DataTab D={null} onPick={pickMonth} onLoaded={afterLoad} />
          </>
        ) : null}

        {D && S && G ? (
          <>
            <Subline>{periodLine(D, S, people)}</Subline>
            <KpiRow items={staffing ? staffingKpis(D, S, G, people) : kpis(D, S, target, people)} />
            {shown === 'man' ? (
              <StaffingTab D={D} S={S} G={G} target={target} dark={dark} showPresence={!staffing}
                people={people} open={desigOpen} setOpen={setDesigOpen} />
            ) : null}
            {shown === 'days' ? (
              <DaysTab D={D} S={S} target={target} dark={dark} setTarget={setTarget} people={people} />
            ) : null}
            {shown === 'dept' ? (
              <DepartmentsTab D={D} S={S} target={target} ui={deptUi} setUi={setDeptUi} people={people} />
            ) : null}
            {shown === 'exc' ? (
              <WorkersTab D={D} S={S} ui={workerUi} setUi={setWorkerUi} people={people} />
            ) : null}
            {shown === 'data' ? <DataTab D={D} onPick={pickMonth} onLoaded={afterLoad} /> : null}
            <Method D={D} people={people} />
          </>
        ) : null}
      </main>
    </>
  )
}

/** The period, day-range and department pickers, once a month is in. */
function Pickers({ D, S, range, setRange, pickMonth, setDept }: {
  D: AttMonth; S: Scope
  range: { from: number | null; to: number | null }
  setRange: (r: { from: number | null; to: number | null }) => void
  pickMonth: (m: string) => void
  setDept: (d: string | null) => void
}) {
  return (
    <>
      <Picker label="Period">
        <select value={D.month} onChange={(e) => pickMonth(e.target.value)}
          style={{ minWidth: 150 }}>
          {D.months.map((m) => <option key={m.month} value={m.month}>{m.label}</option>)}
        </select>
      </Picker>
      <Picker label="Days" gap={8}>
        <select value={S.dFrom}
          onChange={(e) => setRange({ ...range, from: Number(e.target.value) })}>
          {D.days.map((d) => <option key={d.d} value={d.d}>{d.d}</option>)}
        </select>
        <span style={{ fontSize: 13, color: 'var(--color-neutral-600)' }}>to</span>
        <select value={S.dTo}
          onChange={(e) => setRange({ ...range, to: Number(e.target.value) })}>
          {D.days.map((d) => <option key={d.d} value={d.d}>{d.d}</option>)}
        </select>
        {!S.fullRange ? (
          <button className="btn btn-secondary" type="button"
            style={{ fontSize: 12, padding: '6px 12px' }}
            onClick={() => setRange({ from: null, to: null })}>Whole period</button>
        ) : null}
      </Picker>
      <Picker label="Department">
        <select value={S.sel} style={{ minWidth: 170 }}
          onChange={(e) => setDept(e.target.value === 'All' ? null : e.target.value)}>
          {['All', ...D.depts.map((d) => d.name)].map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Picker>
    </>
  )
}

function Picker({ label, gap = 10, children }: {
  label: string; gap?: number; children: React.ReactNode
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap }}>
      <span className="axis-title">{label}</span>
      {children}
    </div>
  )
}

/** The group the page is not showing. */
const other = (people: People): People => (people === 'staff' ? 'workers' : 'staff')

/** 'September 2026 · workers · 30 days · 541 workers active on roll'. */
function periodLine(D: AttMonth, S: Scope, people: People): string {
  return [
    D.label,
    people,
    S.fullRange
      ? `${num(S.nDays)} days`
      : `${S.dFrom}–${S.dTo} (${num(S.nDays)} of ${num(D.dayCount)} days)`,
    S.isAll ? `${heads(S.activeRoll, people)} active on roll` : `${num(S.activeRoll)} active in ${S.sel.toLowerCase()}`,
    D.otherOnFile ? `${num(D.otherOnFile)} ${other(people)} on file are not in these figures` : '',
  ].filter(Boolean).join(' · ')
}

function kpis(D: AttMonth, S: Scope, target: number, people: People): Kpi[] {
  const { req, gapAbs, manPct } = S
  // Staff have no register to miss a punch against and no PP marks.
  const noRegister = people === 'staff'
  return [
    { label: 'Manned strength', value: num(S.presentAvg),
      note: 'avg heads/day · ' + (req ? num(req) + ' sanctioned' : 'no sanctioned figure') },
    req && gapAbs !== null && manPct !== null
      ? { label: 'Manning gap', value: signed(-gapAbs),
          note: `${num(Math.abs(100 - manPct), 1)}% ${gapAbs > 0 ? 'short of' : 'over'} requirement`,
          tone: gapAbs > 0 ? 'bad' : 'good' }
      : { label: 'Manning gap', value: '—', note: 'not on the requirement sheet' },
    { label: 'Attendance', value: num(S.attPct, 1) + '%',
      note: `of ${num(S.activeRoll)} active on roll`, tone: S.attPct >= target ? 'good' : 'bad' },
    { label: 'Late arrivals', value: num(S.latePct, 1) + '%',
      note: `${num(S.totLate)} of ${num(S.presentDays)} present days` },
    noRegister
      ? { label: 'Punch misses', value: '—', note: 'no register for staff, so none to judge against' }
      : { label: 'Punch misses', value: num(S.totMiss),
          note: `present on the register, not both punches · ${num(S.totMiss - S.totFlagged)} with no punch at all` },
    noRegister
      ? { label: 'Overtime', value: '—', note: 'no PP marks for staff, so none is read off' }
      : { label: 'Overtime', value: num(S.totOt),
          note: `hrs, ${D.otHours.PP} per PP mark · ${num(S.totOt / S.nDays)} hrs/day avg` },
  ]
}

/** The Staffing page's four: the roll against the sheet, not the day's
 *  presence. Where the sheet has lines for what is in view the roll is the
 *  heads on those lines; otherwise it is the active roll. */
function staffingKpis(D: AttMonth, S: Scope, G: DesigScope, people: People): Kpi[] {
  const { req } = S
  const W = words(people)
  const onSheet = G.rows.length > 0
  const roll = onSheet ? G.roll : S.activeRoll
  return [
    { label: 'Required', value: req ? num(req) : '—',
      note: D.requirementSheet ? 'per ' + D.requirementSheet : 'sanctioned strength' },
    { label: 'On roll', value: num(roll),
      note: onSheet
        ? `at least one present day · ${W.lines} on the sheet`
        : 'at least one present day in the month' },
    req
      ? { label: 'Roll vs requirement', value: signed(roll - req),
          note: `${num((roll / req) * 100, 0)}% of required heads on roll`,
          tone: roll < req ? 'bad' : 'good' }
      : { label: 'Roll vs requirement', value: '—', note: 'no requirement for this scope' },
    { label: `${W.Lines} short`, value: onSheet ? num(G.short) : '—',
      note: onSheet
        ? `${num(G.over)} over · of ${num(G.rows.length)} on the sheet`
        : `no ${W.line} data for this period`,
      tone: G.short ? 'bad' : 'plain' },
  ]
}

function Method({ D, people }: { D: AttMonth; people: People }) {
  const r = D.reqByShift
  const W = words(people)
  const staff = people === 'staff'
  return (
    <div className="pretty" style={{
      fontSize: 12, color: 'var(--color-neutral-600)', lineHeight: 1.6, maxWidth: '110ch',
      paddingTop: 4,
    }}>
      Source: {staff ? 'the staff biometric report' : 'the workers’ register and the workers’ biometric report'} for {D.label} ({num(D.rowCount)} {W.one}-day
      marks), read against the manpower requirement on file
      {D.requirementSheet ? ` — ${D.requirementSheet} —` : ''} of {num(D.requiredTotal)} {W.many}: {r.A} on the first
      shift, {r.B} on the second, {r.C} on the third, {r.G} general. {staff ? 'Staff' : 'Workers'} only
      {D.otherOnFile ? ` — the ${num(D.otherOnFile)} ${other(people)} on file are left out` : ''}.{' '}
      {staff
        ? 'Staff have no register: present and absent are the biometric report’s own word for the day — ABSENT is A, and a day with a punch or any other remark is P. Shift, punches'
        : 'Present and absent are the register’s marks: P an eight-hour day, PP a twelve-hour one, A absent. Shift, punches'}
      {' '}and the day’s flag come from the biometric. Its shift codes come in two sets — AS/BS/CS/GS
      and CA/CB/CC/CG — which match on punch times and are merged here into shifts A (06:00),
      B (14:00), C (22:00) and General (08:00); 12HRS, stray codes and present days with no shift
      code are grouped as 12-hr / other. The active roll counts the {num(D.activeRoll)} {W.many}
      {staff ? ' the report' : ' the register'} marks present or absent at least once in the period. Late and short-hours
      figures are taken as the biometric flags them and have not been recomputed from punch
      times.{' '}
      {staff
        ? 'With no register there is nothing to miss a punch against, so there are no punch misses; and with no PP marks there is no overtime to read off.'
        : `A punch miss is a day the register marks present without both punches on the biometric — no punch at all, or one of the two. Overtime is not booked in either report: it is ${D.otHours.PP} hours for each PP mark and ${D.otHours.PPP} for a PPP.`}
    </div>
  )
}
